import { useState } from 'react';
import type { CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, EmptyState, Favicon, Icon, Logo, Wordmark } from '@vault/ui';

/** Read-only view returned by GET /v1/share/public/:token — url/title/favicon
 *  only, never captured content bodies. Mirrors the API's PublicShareView. */
interface PublicShareItem {
  url: string;
  title: string;
  favIconUrl: string | null;
  domain?: string;
}
interface PublicShareView {
  kind: 'collection' | 'sweep';
  title: string;
  items: PublicShareItem[];
}

class ShareError extends Error {
  constructor(public readonly notFound: boolean) {
    super(notFound ? 'not_found' : 'load_failed');
  }
}

function safeHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * Public, unauthenticated share page (/s/:token). We call the public endpoint
 * with a plain fetch (no auth header) and render a branded, read-only list.
 * Revoked / expired links resolve to 404 → a friendly dead-end.
 */
export function SharePublicPage() {
  const { token } = useParams();

  const query = useQuery({
    queryKey: ['share', 'public', token],
    enabled: Boolean(token),
    retry: false,
    queryFn: async (): Promise<PublicShareView> => {
      const res = await fetch(`/v1/share/public/${encodeURIComponent(token ?? '')}`);
      if (res.status === 404) throw new ShareError(true);
      if (!res.ok) throw new ShareError(false);
      return (await res.json()) as PublicShareView;
    },
  });

  return (
    <div style={root}>
      <style>{SHARE_CSS}</style>

      <header style={topbar}>
        <a href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
          <Logo size={26} style={{ borderRadius: 8 }} />
          <Wordmark size={15} />
        </a>
        <a href="/" className="tvsh-cta">
          Get Nomad Tabs
          <Icon name="chevron-right" size={12} color="currentColor" />
        </a>
      </header>

      <main style={main}>
        {query.isPending ? (
          <LoadingList />
        ) : query.isError ? (
          <div style={{ paddingTop: 40 }}>
            <EmptyState
              dashed
              icon={<Icon name="lock" size={18} color="var(--text-3)" />}
              title={query.error instanceof ShareError && query.error.notFound ? 'This shared vault isn’t available' : 'We couldn’t load this share'}
              description={
                query.error instanceof ShareError && query.error.notFound
                  ? 'The link may have been revoked or expired. Ask whoever shared it for a fresh one.'
                  : 'Something went wrong fetching this link. Try again in a moment.'
              }
            />
          </div>
        ) : (
          <Shared view={query.data} />
        )}
      </main>

      <footer style={footer}>
        <a href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-3)' }}>
          <Logo size={14} style={{ borderRadius: 4 }} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.06em' }}>MADE WITH NOMAD TABS</span>
        </a>
      </footer>
    </div>
  );
}

function Shared({ view }: { view: PublicShareView }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Badge tone="neutral">{view.kind === 'collection' ? 'COLLECTION' : 'SWEEP'}</Badge>
        <h1 style={title}>{view.title}</h1>
        <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-3)' }}>
          {view.items.length.toLocaleString()} {view.items.length === 1 ? 'TAB' : 'TABS'}
        </span>
      </div>

      {view.items.length === 0 ? (
        <EmptyState title="Nothing here yet" description="This share doesn’t have any tabs." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {view.items.map((item, i) => (
            <a
              key={`${item.url}-${i}`}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="tvsh-row"
              style={row}
            >
              <RowFavicon item={item} index={i} />
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.title || safeHost(item.url)}
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.domain ?? safeHost(item.url)}
                </span>
              </span>
              <Icon name="external" size={13} color="var(--text-3)" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function RowFavicon({ item, index }: { item: PublicShareItem; index: number }) {
  const [broken, setBroken] = useState(false);
  const host = item.domain ?? safeHost(item.url);
  const letter = (host.replace(/^www\./, '')[0] ?? 'A').toUpperCase();
  if (item.favIconUrl && !broken) {
    return (
      <img
        src={item.favIconUrl}
        alt=""
        width={19}
        height={19}
        onError={() => setBroken(true)}
        style={{ borderRadius: 5, flexShrink: 0, background: 'var(--surface-2)' }}
      />
    );
  }
  return <Favicon letter={letter} index={index} size={19} />;
}

function LoadingList() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span className="tv-skeleton" style={{ width: 180, height: 22, borderRadius: 6, marginBottom: 12 }} />
      {Array.from({ length: 6 }).map((_, i) => (
        <span key={i} className="tv-skeleton" style={{ height: 50, borderRadius: 10 }} />
      ))}
    </div>
  );
}

const root: CSSProperties = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg)',
  color: 'var(--text-1)',
  fontFamily: 'var(--font-body)',
};
const topbar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '16px 24px',
  borderBottom: '1px solid var(--line)',
  background: 'var(--surface-0)',
};
const main: CSSProperties = { flex: 1, width: '100%', maxWidth: 760, margin: '0 auto', padding: '28px 20px 48px' };
const footer: CSSProperties = { display: 'flex', justifyContent: 'center', padding: '20px 24px', borderTop: '1px solid var(--line)' };
const title: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-display-cond)',
  fontSize: 24,
  fontWeight: 700,
  letterSpacing: '.02em',
  textTransform: 'uppercase',
};
const row: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 14px',
  background: 'var(--surface-1)',
  border: '1px solid var(--line)',
  borderRadius: 10,
};

const SHARE_CSS = `
.tvsh-cta{margin-left:auto;display:inline-flex;align-items:center;gap:6px;height:34px;padding:0 16px;border-radius:999px;
  background:var(--primary);color:#fff;font-size:12.5px;font-weight:600}
.tvsh-cta:hover{background:var(--primary-hover)}
.tvsh-row{transition:border-color .15s var(--ease),background .15s var(--ease)}
.tvsh-row:hover{border-color:var(--line-strong);background:var(--surface-2)}
`;
